import { FC } from "react";

import { BarEvent, EventsBar } from "@nens/chakra/components";
import { Lateral } from "@nens/threedi/openapi-client";

import { GridCard } from "../../../components/GridCard";

function eventValuesToBlocks(
  values: number[][],
  duration: number | undefined,
  offset: number
): (BarEvent & { value: number })[] {
  return values
    .map(([start, value], idx): BarEvent & { value: number } => {
      // Let each event run until the timestamp of the next change, or
      // until 'duration'.
      const realStart = start + offset;
      const end =
        idx + 1 < values.length
          ? values[idx + 1][0] + offset
          : offset + (duration ?? values[idx][0]);
      return { start: realStart, end, value };
    })
    .filter(
      // Filter out empty events (especially if duration equals
      // timestamp of the last event, or is undefined)
      // Also filter out events without rain.
      (event) => event.start !== event.end && Math.abs(event.value) > 0
    );
}

function rainToHuman(value: number, unit: string) {
  if (unit === "m/s") {
    return `${Math.round(value * 1000 * 3600)} mm/hr`;
  } else {
    return `${value} ${unit}`;
  }
}

interface LateralsEventProps {
  simulationDuration: number;
  events?: Lateral[];
}

export const LateralsEvent: FC<LateralsEventProps> = ({ simulationDuration, events }) => {
  if (!events || !events.length) return null;

  const barEvents: BarEvent[][] = events.map((lateralRainEvent) => {
    return eventValuesToBlocks(
      lateralRainEvent.values,
      lateralRainEvent.duration ?? 0,
      lateralRainEvent.offset
    ).map((block) => {
      return {
        start: block.start,
        end: block.end,
        title: lateralRainEvent.constant ? "Constant lateral" : "Lateral",
        content: "Amount: " + rainToHuman(block.value, lateralRainEvent.units),
        color: "blue.500",
        value: block.value,
      };
    });
  });

  return (
    <GridCard variant="twocolumns" title="Lateral">
      <EventsBar
        start={0}
        end={simulationDuration}
        events={barEvents.flat()}
        height="30px"
        borderColor="brand.grey.600"
        plotAboveAndBelowYAxis
      />
    </GridCard>
  );
};
